import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const steps = [
    {
        marker: '①',
        title: 'You can see what\'s happening',
        description:
            'Who\'s where, which visits are done, what\'s pending. Without calling anyone.',
    },
    {
        marker: '②',
        title: 'You know the session happened',
        description:
            "The therapist checks in at the patient's home. That's your record, not what they told you later.",
    },
    {
        marker: '③',
        title: 'The notes are done before they leave the patient\'s home',
        description:
            "Not that evening. Not the next morning. Right there, before the next visit.",
    },
    {
        marker: '④',
        title: 'Month-end isn\'t a fight anymore',
        description:
            'Every payout ties back to a verified visit. The record settles it.',
    },
];

const FeaturesSection = () => {
    return (
        <Box
            component="section"
            id="how-it-works"
            aria-labelledby="how-it-works-heading"
            sx={{
                py: { xs: 7, md: 8, lg: 8.5 },
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F8FF 50%, #FAFBFF 100%)',
                borderTop: '1px solid rgba(0, 102, 255, 0.08)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background:
                        'linear-gradient(90deg, transparent, rgba(0, 102, 255, 0.25), rgba(99, 102, 241, 0.2), transparent)',
                    opacity: 0.9,
                },
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto'}}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            textAlign: { xs: 'center', md: 'left' },
                            color: '#0066FF',
                            bgcolor: 'rgba(0, 102, 255, 0.1)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2.5,
                        }}
                    >
                        How it works
                    </Typography>
                </Box>
                <Typography
                    id="how-it-works-heading"
                    variant="h2"
                    sx={{
                        fontSize: { xs: '1.65rem', sm: '2rem', md: '2.35rem', lg: '2.5rem' },
                        fontWeight: 700,
                        lineHeight: 1.2,
                        letterSpacing: '-0.03em',
                        color: '#0F172A',
                        textAlign: { xs: 'left', md: 'center' },
                        mb: { xs: 4, md: 6 },
                        maxWidth: { md: '28ch' },
                        mx: { md: 'auto' },
                    }}
                >
                    The same operation. Just one where you're in control.
                </Typography>

                {/* Horizontal flow (md+) */}
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        width: '100%',
                    }}
                >
                    {steps.map((step, i) => (
                        <React.Fragment key={step.title}>
                            <Box
                                sx={{
                                    flex: '1 1 0',
                                    minWidth: 0,
                                    px: { md: 2, lg: 3 },
                                    textAlign: 'center',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '1.75rem',
                                        lineHeight: 1,
                                        mb: 1.5,
                                        color: '#0066FF',
                                        fontFamily: 'Georgia, "Times New Roman", serif',
                                    }}
                                    aria-hidden
                                >
                                    {step.marker}
                                </Typography>
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontSize: '1.125rem',
                                        fontWeight: 700,
                                        color: '#0F172A',
                                        mb: 1.5,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.65,
                                        color: 'rgba(15, 23, 42, 0.78)',
                                        textAlign: 'center',
                                        m: 0,
                                    }}
                                >
                                    {step.description}
                                </Typography>
                            </Box>
                            {i < steps.length - 1 && (
                                <Box
                                    sx={{
                                        width: '1px',
                                        flexShrink: 0,
                                        alignSelf: 'stretch',
                                        minHeight: 120,
                                        background:
                                            'linear-gradient(180deg, transparent 0%, rgba(0, 102, 255, 0.18) 15%, rgba(0, 102, 255, 0.18) 85%, transparent 100%)',
                                    }}
                                    aria-hidden
                                />
                            )}
                        </React.Fragment>
                    ))}
                </Box>

                {/* Vertical flow (mobile / tablet) */}
                <Box
                    component="ol"
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        flexDirection: 'column',
                        gap: 0,
                        listStyle: 'none',
                        m: 0,
                        p: 0,
                        pl: 0,
                    }}
                >
                    {steps.map((step, i) => (
                        <Box
                            component="li"
                            key={step.title}
                            sx={{
                                display: 'flex',
                                gap: 2,
                                pb: i < steps.length - 1 ? 3 : 0,
                                mb: i < steps.length - 1 ? 3 : 0,
                                borderBottom:
                                    i < steps.length - 1
                                        ? '1px solid rgba(0, 102, 255, 0.1)'
                                        : 'none',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: 44,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '1.5rem',
                                        lineHeight: 1,
                                        color: '#0066FF',
                                        fontFamily: 'Georgia, "Times New Roman", serif',
                                    }}
                                    aria-hidden
                                >
                                    {step.marker}
                                </Typography>
                                {i < steps.length - 1 && (
                                    <Box
                                        sx={{
                                            flex: 1,
                                            width: '2px',
                                            minHeight: 24,
                                            mt: 1.5,
                                            mb: -0.5,
                                            background: 'rgba(0, 102, 255, 0.2)',
                                            borderRadius: 1,
                                        }}
                                        aria-hidden
                                    />
                                )}
                            </Box>
                            <Box sx={{ minWidth: 0, pt: 0.25 }}>
                                <Typography
                                    component="h3"
                                    sx={{
                                        fontSize: '1.0625rem',
                                        fontWeight: 700,
                                        color: '#0F172A',
                                        mb: 1,
                                        letterSpacing: '-0.02em',
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.7,
                                        color: 'rgba(15, 23, 42, 0.78)',
                                        m: 0,
                                    }}
                                >
                                    {step.description}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

export default FeaturesSection;
